/**
 * Demo harness entry point.
 *
 * Boots the Pearl shell on a plain HTML page (no Chrome extension context).
 * Wires the demo-only `createDemoShell` factory to:
 *   - `chromeShim` for `chrome.runtime` / `chrome.storage` lookalikes
 *   - the sandbox URL rewriter (every product URL goes to `sandbox.*`)
 *   - a small toolbar for context IDs, feature flags, inline-panel toggle,
 *     disconnect, and optional login popup (shell boots connected)
 *   - iframe preloading for non-Voice Pearl URLs (sandbox `buildUrl`); the
 *     pool refreshes when the patient dropdown changes (immediately) or when
 *     Office ID text changes (debounced), if those controls exist
 *
 * This file lives outside `src/` and is bundled by `demo/vite.config.ts` into
 * `demo/dist/standalone.js`. Nothing in the production extension imports
 * from `demo/`.
 */

import { createDemoShell, type PearlShell } from './demo-shell';
import { chromeShim } from './chrome-shim';
import {
  buildUrl as sandboxBuildUrl,
  SANDBOX_VOICE_URL,
  rewriteToSandbox,
} from './sandbox-urls';
import type { FeatureFlags } from '../../src/services/configuration';
import { createIframePreloader, type IframePreloader } from '../../src/pearl-shell/iframe-preloader';
import type { BuildUrlContext } from '../../src/pearl-shell/url-builder';
import { PEARL_ACTIONS } from '../../src/pearl-shell/icon-config';
import { DEMO_PATIENT_OPTIONS, resolveStaticDemoPatient } from './demo-patients';
import { renderDemoPatientProfile } from './demo-patient-profile';
import { MSG } from '../../src/contracts/index';
import { wireEpmsDemoPage } from './epms-harness';
import { wireDentrixSchedule } from './dentrix-schedule';

interface DemoState {
  patientId: string;
  officeId: string;
  /** Display name for the current patient (dropdown label or static lookup). */
  patientName: string;
  inlinePanelEnabled: boolean;
  flags: FeatureFlags;
}

const state: DemoState = {
  patientId: '',
  officeId: '',
  patientName: '',
  inlinePanelEnabled: true,
  flags: {
    sodActive: true,
    piActive: true,
    insuranceActive: true,
    voiceActive: true,
  },
};

let shell: PearlShell | null = null;
let preloader: IframePreloader | null = null;

// ─── Demo-only: send patient context to Voice iframe ───────────────────

function isEpmsDemoPage(): boolean {
  return window.location.pathname.includes('/epms');
}

function detectPmsFromPage(): { pmsId: string; pmsName: string } {
  if (isEpmsDemoPage()) return { pmsId: 'epms', pmsName: 'EPMS' };
  if (window.location.pathname.includes('/curve')) return { pmsId: 'curve', pmsName: 'Curve Dental' };
  if (window.location.pathname.includes('/carestack')) return { pmsId: 'carestack', pmsName: 'CareStack' };
  if (window.location.pathname.includes('/opendental')) return { pmsId: 'opendental', pmsName: 'Open Dental' };
  if (window.location.pathname.includes('/dentrix')) return { pmsId: 'dentrix', pmsName: 'Dentrix' };
  if (window.location.pathname.includes('/dexis')) return { pmsId: 'dexis', pmsName: 'DEXIS' };
  return { pmsId: 'denticon', pmsName: 'Denticon' };
}

function buildPmsDetectedMessage(): Record<string, unknown> {
  const pms = detectPmsFromPage();
  return {
    type: MSG.PMS_DETECTED,
    pmsId: pms.pmsId,
    pmsName: pms.pmsName,
    patientContext: state.patientId
      ? { patientId: state.patientId, patientName: state.patientName }
      : null,
  };
}

function sendPmsDetectedToPanel(): void {
  if (!shell) return;
  const body = shell.getPanelBody();
  const iframes = body.querySelectorAll('iframe');
  const msg = buildPmsDetectedMessage();
  // Only Voice consumes PMS_DETECTED; posting to the other preloaded
  // iframes just logs origin-mismatch errors while they are still loading.
  const voiceOrigin = new URL(SANDBOX_VOICE_URL).origin;
  for (let i = 0; i < iframes.length; i++) {
    const iframe = iframes[i] as HTMLIFrameElement;
    if (!iframe.contentWindow || !iframe.src) continue;
    try {
      const origin = new URL(iframe.src).origin;
      if (origin !== voiceOrigin) continue;
      iframe.contentWindow.postMessage(msg, origin);
    } catch { /* cross-origin or no contentWindow */ }
  }
}

// ─── Demo-only: expand panel width for perio chart ─────────────────────

const PERIO_WIDTH_THRESHOLD = 500;

function setPerioMode(active: boolean): void {
  if (!shell) return;
  const pnl = shell.getPanelBody().parentElement;
  if (pnl) pnl.classList.toggle('pw-pnl-perio', active);
}

// ─── Demo-only: message listener for iframe events ─────────────────────

window.addEventListener('message', (event) => {
  if (!event.data?.type) return;
  if (event.data.type === MSG.EMBEDDED_READY) {
    setTimeout(() => sendPmsDetectedToPanel(), 100);
  }
  if (event.data.type === MSG.EMBEDDED_RESIZE && typeof event.data.width === 'number' && event.data.width >= PERIO_WIDTH_THRESHOLD) {
    setPerioMode(event.data.width > PERIO_WIDTH_THRESHOLD);
  }
});

/** All actions (including Voice) get hidden preload iframes in the demo. */
const DEMO_PRELOAD_ACTIONS = PEARL_ACTIONS;

const PRELOADER_REFRESH_DEBOUNCE_MS = 400;
let preloaderRefreshTimer: ReturnType<typeof setTimeout> | null = null;

function buildPreloadContext(): BuildUrlContext {
  return {
    patientId: state.patientId || null,
    officeId: state.officeId || null,
    pathname: window.location.pathname,
    href: window.location.href,
    insuranceUrlOverride: shell?.getInsuranceUrlOverride() ?? undefined,
    voiceUrl: SANDBOX_VOICE_URL,
  };
}

/**
 * Creates (or recreates) hidden preload iframes under the panel body using
 * the same sandbox `buildUrl` as the shell. No-op when inline panel is off.
 */
function mountPreloader(): void {
  if (!shell || !state.inlinePanelEnabled) {
    preloader?.destroy();
    preloader = null;
    return;
  }
  preloader?.destroy();
  const body = shell.getPanelBody();
  preloader = createIframePreloader(document, body, { buildUrlImpl: sandboxBuildUrl });
  preloader.create(DEMO_PRELOAD_ACTIONS, buildPreloadContext());
}

/** Sync refresh when the patient dropdown changes (no debounce). */
function refreshPreloaderImmediate(): void {
  if (!state.inlinePanelEnabled || !preloader) return;
  preloader.refresh(buildPreloadContext());
}

/**
 * Debounced refresh for text fields (e.g. office ID) so typing does not
 * reload on every keystroke.
 */
function schedulePreloaderRefresh(): void {
  if (!state.inlinePanelEnabled || !preloader) return;
  if (preloaderRefreshTimer != null) {
    clearTimeout(preloaderRefreshTimer);
  }
  preloaderRefreshTimer = setTimeout(() => {
    preloaderRefreshTimer = null;
    preloader?.refresh(buildPreloadContext());
  }, PRELOADER_REFRESH_DEBOUNCE_MS);
}

/**
 * Mount the Pearl shell with all dependencies wired against the demo state.
 *
 * `getInlinePanelEnabled` and `getPatientOfficeIds` read live from `state`
 * so the toolbar can edit them without remounting the shell. Every URL
 * destined for the inline panel or new-tab opener flows through
 * `rewriteToSandbox` (also baked into `buildUrl` inside `demo-shell.ts`).
 */
function mountShell(): void {
  shell = createDemoShell(document, {
    getSandboxEnabled: async () => false,
    getInlinePanelEnabled: async () => state.inlinePanelEnabled,
    getPatientOfficeIds: () => ({
      patientId: state.patientId || null,
      officeId: state.officeId || null,
    }),
    getPathname: () => window.location.pathname,
    getHref: () => window.location.href,
    getIconUrl: (name) => chromeShim.runtime.getURL(`icons/widget/${name}.svg`),
    getVoiceUrl: () => SANDBOX_VOICE_URL,
    onVoiceClick: () => {
      // No-op: the demo shell routes Voice through panel.open() like all
      // other actions instead of the dedicated voice-iframe lifecycle.
    },
    openTabInBackground: (url) => {
      window.open(rewriteToSandbox(url), '_blank');
    },
    onMinimizedChange: () => {
      // No persistence in the demo — the toolbar can re-mount if needed.
    },
    getPreloadedIframe: (url) => preloader?.claimForOpen(url) ?? null,
  });

  // Demo boots already connected (no probe / simulate-login flow).
  shell.setFeatureFlags(state.flags);
  shell.setConnected(true);
}

// ─── Demo toolbar ──────────────────────────────────────────────────────

function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

function renderFlags(): void {
  for (const key of ['sodActive', 'piActive', 'insuranceActive', 'voiceActive'] as const) {
    const el = $(`flag-${key}`) as HTMLInputElement | null;
    if (el) el.checked = state.flags[key];
  }
}

/**
 * Wires the toolbar controls in `demo/index.html` to live demo state.
 * Every input either updates `state` (which the shell reads on next call)
 * or fires an action immediately.
 */
function wireToolbar(): void {
  const patientSelect = $('patient-select') as HTMLSelectElement | null;
  const officeInput = $('office-id') as HTMLInputElement | null;
  const inlineToggle = $('inline-panel-toggle') as HTMLInputElement | null;

  if (patientSelect) {
    for (const row of DEMO_PATIENT_OPTIONS) {
      const opt = document.createElement('option');
      opt.value = row.patientId;
      opt.textContent = row.fullName;
      opt.setAttribute('data-office-id', row.officeId);
      patientSelect.appendChild(opt);
    }
    patientSelect.value = state.patientId;
    const syncPatientProfile = (): void => {
      const opt = patientSelect.selectedOptions[0];
      const patientId = patientSelect.value.trim();
      const fullName = opt?.textContent?.trim() || '';
      renderDemoPatientProfile(patientId, fullName);
    };
    syncPatientProfile();
    patientSelect.addEventListener('change', () => {
      const opt = patientSelect.selectedOptions[0];
      state.patientId = patientSelect.value.trim();
      state.officeId = opt?.getAttribute('data-office-id')?.trim() || '';
      state.patientName = state.patientId ? opt?.textContent?.trim() || '' : '';
      syncPatientProfile();
      refreshPreloaderImmediate();
      sendPmsDetectedToPanel();
    });
  }
  if (officeInput) {
    officeInput.value = state.officeId;
    officeInput.addEventListener('input', () => {
      state.officeId = officeInput.value.trim();
      schedulePreloaderRefresh();
    });
  }
  if (inlineToggle) {
    inlineToggle.checked = state.inlinePanelEnabled;
    inlineToggle.addEventListener('change', () => {
      state.inlinePanelEnabled = inlineToggle.checked;
      mountPreloader();
    });
  }

  for (const key of ['sodActive', 'piActive', 'insuranceActive', 'voiceActive'] as const) {
    const el = $(`flag-${key}`) as HTMLInputElement | null;
    if (!el) continue;
    el.checked = state.flags[key];
    el.addEventListener('change', () => {
      state.flags = { ...state.flags, [key]: el.checked };
      if (shell?.isConnected()) {
        shell.setFeatureFlags(state.flags);
        preloader?.refresh(buildPreloadContext());
      }
    });
  }

  $('btn-reconnect')?.addEventListener('click', () => {
    if (!shell) return;
    shell.setFeatureFlags(state.flags);
    shell.setConnected(true);
    shell.closeLoginPopup();
    renderFlags();
    if (state.inlinePanelEnabled) {
      if (preloader) preloader.refresh(buildPreloadContext());
      else mountPreloader();
    }
  });

  $('btn-disconnect')?.addEventListener('click', () => {
    shell?.setConnected(false);
  });

  $('btn-open-login-popup')?.addEventListener('click', () => {
    shell?.openLoginPopup();
  });
}

/**
 * Fixed patient context for pages without a `#patient-select` dropdown (see
 * `resolveStaticDemoPatient`). Unknown or absent ids leave the page
 * patient-less — buttons open generic product pages and name slots show
 * their `data-empty-label` instead of the baked-in screenshot name.
 */
function initStaticPatient(): void {
  const row = resolveStaticDemoPatient(
    window.location.search,
    document.body.dataset.demoPatientId,
  );
  if (!row) {
    renderDemoPatientProfile('', '');
    return;
  }
  state.patientId = row.patientId;
  state.officeId = row.officeId;
  state.patientName = row.fullName;
  renderDemoPatientProfile(row.patientId, row.fullName);
}

function boot(): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
    return;
  }
  if (isEpmsDemoPage()) {
    wireToolbar();
    wireEpmsDemoPage(document);
    return;
  }
  initStaticPatient();
  wireDentrixSchedule(document);
  mountShell();
  mountPreloader();
  wireToolbar();
}

boot();
